/**
 * stepTypeColors.ts - Step type to border color mapping
 *
 * Purpose:
 *   Maps automation step types to their corresponding border colors.
 *   Colors match the icon colors used in AutomationStepIcon.
 *
 * Usage:
 *   import { getStepTypeBorderColor } from '../utils/stepTypeColors';
 *   const borderColor = getStepTypeBorderColor(stepType);
 *
 * Related:
 *   - AutomationStepIcon/index.tsx (icon colors)
 *   - stepNodeStyles.ts (node styling)
 *   - AutomationStepNodeWrapper.tsx (applies border colors)
 */

import * as Api from '@ViewModels';
import {
	actionItemTintColor,
	brandPrimary,
	brandSecondary,
	campaignTintColor,
	emailActivityTintColor,
	newsletterActivityTintColor,
	orange,
	waitGray,
} from '../../../styles/colors';

/**
 * Returns the border color for a given automation step type.
 * Colors match the icon background colors used in AutomationStepIcon.
 */
export function getStepTypeBorderColor(stepType: Api.AutomationStepType | string): string {
	switch (stepType) {
		case Api.AutomationStepType.Email:
			return emailActivityTintColor;
		case Api.AutomationStepType.HtmlNewsletter:
			return newsletterActivityTintColor;
		case Api.AutomationStepType.ActionItem:
		case Api.AutomationStepType.Opportunity:
			return actionItemTintColor;
		case Api.AutomationStepType.AddTag:
			return brandPrimary;
		case Api.AutomationStepType.RemoveTag:
			return orange;
		case Api.AutomationStepType.Texting:
			return brandSecondary;
		case Api.AutomationStepType.HandwrittenCard:
			return campaignTintColor;
		case Api.AutomationStepType.NoAction:
			return waitGray;
		case 'TriggerAutomation':
			return brandPrimary;
		case Api.AutomationStepType.Switch:
		default:
			return brandPrimary;
	}
}

/**
 * Returns the indicator circle color for a given automation step type.
 * These colors appear as a colored circle on the left side of the node content.
 *
 * Color mapping:
 *   - Green (#89C947): Email, Newsletter, Action Item, Opportunity, Conditions
 *   - Blue (#3E8BFE): Texting
 *   - Purple (#6E27A3): Handwritten Cards
 *   - Blue (#0466FF): Tags, Triggers
 *   - Orange (#F89143): Remove Tag
 *   - Gray (#BEBEBE): Wait/Delay
 */
export function getStepTypeIndicatorColor(stepType: Api.AutomationStepType | string): string {
	switch (stepType) {
		// Green indicator for email and related communication
		case Api.AutomationStepType.Email:
		case Api.AutomationStepType.HtmlNewsletter:
		case Api.AutomationStepType.ActionItem:
		case Api.AutomationStepType.Opportunity:
		case Api.AutomationStepType.Switch: // Conditions/branching use green
			return newsletterActivityTintColor; // #89C947 - green

		case 'TriggerAutomation':
			return brandPrimary;

		// Blue indicator for texting
		case Api.AutomationStepType.Texting:
			return brandSecondary; // #3E8BFE - light blue

		// Purple indicator for cards
		case Api.AutomationStepType.HandwrittenCard:
			return campaignTintColor; // #6E27A3 - purple

		// Blue indicator for tags
		case Api.AutomationStepType.AddTag:
			return brandPrimary; // #0466FF - deep blue

		// Orange indicator for remove tag
		case Api.AutomationStepType.RemoveTag:
			return orange; // #F89143 - orange

		// Gray indicator for wait/no action
		case Api.AutomationStepType.NoAction:
			return waitGray; // gray

		default:
			return brandPrimary; // #0466FF - deep blue
	}
}

/**
 * Indicator color for trigger nodes
 */
export const TRIGGER_INDICATOR_COLOR = brandPrimary; // #0466FF - deep blue

/**
 * Indicator color for condition nodes
 */
export const CONDITION_INDICATOR_COLOR = newsletterActivityTintColor; // #89C947 - green
